import { useState } from "react";

/* ── IDEA Protocol: Identify → Discuss → Estimate → Aggregate ─── */

interface ExpertEstimate {
  expertId: string;
  name: string;
  initials: string;
  color: string;
  low: number;
  median: number;
  high: number;
  certainty: number;
  reasoning: string;
  round: 1 | 2;
}

interface Variable {
  name: string;
  type: string;
  levels: string;
}

interface Props {
  variable: Variable;
  onComplete: (aggregated: { mean: number; std: number; certainty: number }) => void;
  onBack: () => void;
}

function generateExperts(varName: string): ExpertEstimate[] {
  const short = varName.replace(/_/g, " ").toLowerCase();
  return [
    {
      expertId: "sh", name: "Sarah Hartwell", initials: "SH", color: "rgba(196,181,253,0.5)",
      low: 0.22, median: 0.45, high: 0.72, certainty: 0.78,
      reasoning: `In my 15 years of family law practice, ${short} is consistently one of the strongest predictors of custody outcomes. The relationship with favorable placement decisions is robust across both contested and uncontested cases...`,
      round: 1,
    },
    {
      expertId: "mk", name: "Dr. Michael Kwan", initials: "MK", color: "rgba(134,239,172,0.5)",
      low: 0.10, median: 0.32, high: 0.55, certainty: 0.62,
      reasoning: `Our child psychology research shows ${short} is a real signal, but it's heavily confounded with socioeconomic factors and parental resources. The isolated causal effect on custody outcomes is probably smaller than the raw correlation suggests...`,
      round: 1,
    },
    {
      expertId: "rp", name: "Hon. Rebecca Paulson", initials: "RP", color: "rgba(253,224,71,0.5)",
      low: 0.20, median: 0.40, high: 0.60, certainty: 0.72,
      reasoning: `From my experience presiding over hundreds of custody cases, ${short} shows remarkably consistent effects on child welfare outcomes. I'd place the effect between the other estimates — the relationship is real but has important nonlinearities depending on the child's age and developmental stage...`,
      round: 1,
    },
  ];
}

function generateRound2(varName: string): ExpertEstimate[] {
  const short = varName.replace(/_/g, " ").toLowerCase();
  return [
    {
      expertId: "sh", name: "Sarah Hartwell", initials: "SH", color: "rgba(196,181,253,0.5)",
      low: 0.24, median: 0.42, high: 0.65, certainty: 0.80,
      reasoning: `Dr. Kwan's confounding concerns are valid — I've narrowed my range. But after handling 500+ custody cases, I still believe ${short} has a substantial direct effect on custody outcomes, even after controlling for income and resources.`,
      round: 2,
    },
    {
      expertId: "mk", name: "Dr. Michael Kwan", initials: "MK", color: "rgba(134,239,172,0.5)",
      low: 0.15, median: 0.36, high: 0.52, certainty: 0.67,
      reasoning: `Judge Paulson's courtroom evidence is compelling — I'm revising upward. The effect of ${short} on custody outcomes is probably larger than my initial estimate, especially in cases involving younger children.`,
      round: 2,
    },
    {
      expertId: "rp", name: "Hon. Rebecca Paulson", initials: "RP", color: "rgba(253,224,71,0.5)",
      low: 0.22, median: 0.38, high: 0.56, certainty: 0.74,
      reasoning: `Good convergence. My estimate for ${short} is stable. The group has captured the uncertainty around confounders well. This prior should be well-calibrated for the custody outcome model.`,
      round: 2,
    },
  ];
}

type Step = "identify" | "discuss" | "estimate" | "aggregate";
const STEPS: { key: Step; label: string; desc: string }[] = [
  { key: "identify", label: "Identify", desc: "Experts provide independent estimates" },
  { key: "discuss", label: "Discuss", desc: "Review anonymous estimates, share reasoning" },
  { key: "estimate", label: "Estimate", desc: "Revised estimates after discussion" },
  { key: "aggregate", label: "Aggregate", desc: "Mathematical combination of beliefs" },
];

function gaussianPdf(x: number, mu: number, sigma: number): number {
  return Math.exp(-0.5 * ((x - mu) / sigma) ** 2) / (sigma * Math.sqrt(2 * Math.PI));
}

function ExpertCard({ expert, anonymous, showReasoning }: { expert: ExpertEstimate; anonymous: boolean; showReasoning: boolean }) {
  return (
    <div className="animate-slide-up" style={{
      padding: "16px 20px", borderRadius: 16,
      background: "var(--bg)", border: "1px solid var(--border-muted)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: anonymous ? "rgba(0,0,0,0.06)" : expert.color,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 500, color: "var(--text-primary)",
        }}>
          {anonymous ? "?" : expert.initials}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            {anonymous ? `Expert ${expert.expertId.toUpperCase()}` : expert.name}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
            Round {expert.round} {expert.round === 2 ? "(revised)" : "(initial)"}
          </div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={{
            fontSize: 10, fontWeight: 500, padding: "2px 10px", borderRadius: 120,
            background: expert.certainty > 0.65 ? "rgba(134,239,172,0.15)" : "rgba(253,224,71,0.15)",
            color: expert.certainty > 0.65 ? "rgba(22,163,74,0.9)" : "rgba(161,98,7,0.9)",
          }}>
            {(expert.certainty * 100).toFixed(0)}% certain
          </span>
        </div>
      </div>

      {/* Distribution visualization — SHELF style quartile marks */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ position: "relative", height: 32, background: "rgba(0,0,0,0.02)", borderRadius: 8, overflow: "hidden" }}>
          {/* Range bar */}
          <div style={{
            position: "absolute",
            left: `${expert.low * 100}%`,
            right: `${(1 - expert.high) * 100}%`,
            top: 8, bottom: 8,
            background: expert.color,
            borderRadius: 4,
            opacity: 0.6,
          }} />
          {/* Median mark */}
          <div style={{
            position: "absolute",
            left: `${expert.median * 100}%`,
            top: 4, bottom: 4,
            width: 2, background: "var(--text-primary)",
            borderRadius: 1,
            transform: "translateX(-1px)",
          }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10, color: "var(--text-muted)" }}>
          <span>Low: {expert.low.toFixed(2)}</span>
          <span style={{ fontWeight: 500, color: "var(--text-primary)" }}>Median: {expert.median.toFixed(2)}</span>
          <span>High: {expert.high.toFixed(2)}</span>
        </div>
      </div>

      {/* Reasoning */}
      {showReasoning && (
        <div style={{
          padding: "10px 14px", borderRadius: 10,
          background: "rgba(0,0,0,0.02)", border: "1px solid var(--border-muted)",
          fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.6,
          fontStyle: "italic",
        }}>
          "{expert.reasoning}"
        </div>
      )}
    </div>
  );
}

function AggregationChart({ experts }: { experts: ExpertEstimate[] }) {
  // Linear pool (equal weights)
  const nPoints = 80;
  const xMin = 0;
  const xMax = 0.8;
  const points: { x: number; individual: number[]; pooled: number }[] = [];

  for (let i = 0; i < nPoints; i++) {
    const x = xMin + (xMax - xMin) * (i / (nPoints - 1));
    const individual = experts.map((e) => {
      const sigma = (e.high - e.low) / 4; // approximate
      return gaussianPdf(x, e.median, Math.max(sigma, 0.05));
    });
    const pooled = individual.reduce((a, b) => a + b, 0) / individual.length;
    points.push({ x, individual, pooled });
  }

  const maxPooled = Math.max(...points.map((p) => p.pooled));
  const maxIndividual = Math.max(...points.flatMap((p) => p.individual));
  const maxY = Math.max(maxPooled, maxIndividual);

  const chartH = 140;
  const chartW = 500;

  const toPath = (values: number[]) => {
    return values.map((y, i) => {
      const px = (i / (nPoints - 1)) * chartW;
      const py = chartH - (y / maxY) * (chartH - 10);
      return `${i === 0 ? "M" : "L"} ${px} ${py}`;
    }).join(" ");
  };

  // Aggregated mean and std
  const aggMean = experts.reduce((a, e) => a + e.median, 0) / experts.length;
  const aggStd = Math.sqrt(experts.reduce((a, e) => a + ((e.high - e.low) / 4) ** 2, 0) / experts.length);

  return (
    <div>
      <div className="card" style={{ padding: "20px 24px 12px", borderRadius: 16 }}>
        <svg width="100%" viewBox={`0 0 ${chartW} ${chartH}`} style={{ overflow: "visible" }}>
          {/* Individual expert distributions (faded) */}
          {experts.map((e, idx) => (
            <path
              key={e.expertId}
              d={toPath(points.map((p) => p.individual[idx]))}
              fill="none"
              stroke={e.color.replace("0.5", "0.3")}
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
          ))}
          {/* Pooled distribution (bold) */}
          <path
            d={toPath(points.map((p) => p.pooled))}
            fill="none"
            stroke="var(--text-primary)"
            strokeWidth={2.5}
          />
          {/* Fill under pooled */}
          <path
            d={`${toPath(points.map((p) => p.pooled))} L ${chartW} ${chartH} L 0 ${chartH} Z`}
            fill="rgba(0,0,0,0.04)"
          />
          {/* Aggregated mean line */}
          <line
            x1={aggMean / xMax * chartW}
            y1={0}
            x2={aggMean / xMax * chartW}
            y2={chartH}
            stroke="var(--text-primary)"
            strokeWidth={1}
            strokeDasharray="4 2"
          />
        </svg>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
        {experts.map((e) => (
          <div key={e.expertId} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 12, height: 2, background: e.color, borderRadius: 1 }} />
            <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{e.name}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 2, background: "var(--text-primary)", borderRadius: 1 }} />
          <span style={{ fontSize: 10, color: "var(--text-primary)", fontWeight: 500 }}>Linear Pool</span>
        </div>
      </div>

      {/* Aggregated parameters */}
      <div className="grid-cells" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr", marginTop: 16 }}>
        {[
          { label: "Pooled \u03bc", value: aggMean.toFixed(4) },
          { label: "Pooled \u03c3", value: aggStd.toFixed(4) },
          { label: "Avg Certainty", value: `${(experts.reduce((a, e) => a + e.certainty, 0) / experts.length * 100).toFixed(0)}%` },
          { label: "N Experts", value: String(experts.length) },
        ].map((item) => (
          <div key={item.label} style={{ padding: 14 }}>
            <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 3, fontWeight: 500 }}>{item.label}</div>
            <div style={{ fontSize: 15, fontWeight: 300, color: "var(--text-primary)" }}>{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ElicitationProtocol({ variable, onComplete, onBack }: Props) {
  const [currentStep, setCurrentStep] = useState<Step>("identify");
  const stepIdx = STEPS.findIndex((s) => s.key === currentStep);

  const round1Experts = generateExperts(variable.name);
  const round2Experts = generateRound2(variable.name);
  const aggMean = round2Experts.reduce((a, e) => a + e.median, 0) / round2Experts.length;
  const aggStd = Math.sqrt(round2Experts.reduce((a, e) => a + ((e.high - e.low) / 4) ** 2, 0) / round2Experts.length);
  const aggCertainty = round2Experts.reduce((a, e) => a + e.certainty, 0) / round2Experts.length;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid var(--border-muted)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 400, margin: "0 0 4px" }}>IDEA Protocol</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="tag" style={{ fontSize: 11 }}>{variable.name}</span>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                {variable.type} &middot; {variable.levels}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="tag" style={{ fontSize: 10 }}>SHELF Framework</span>
            <span className="tag" style={{ fontSize: 10 }}>3 Experts</span>
          </div>
        </div>
      </div>

      {/* IDEA step indicators */}
      <div style={{ padding: "16px 32px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {STEPS.map((step, i) => {
            const isActive = i === stepIdx;
            const isComplete = i < stepIdx;
            return (
              <div
                key={step.key}
                onClick={() => i <= stepIdx && setCurrentStep(step.key)}
                className={`pipeline-step ${isActive ? "active" : isComplete ? "complete" : ""}`}
                style={{ flex: 1, cursor: i <= stepIdx ? "pointer" : "default" }}
              >
                <div className={`step-number ${isActive ? "active" : isComplete ? "complete" : "pending"}`}>
                  {isComplete ? "\u2713" : i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: isActive || isComplete ? "var(--text-primary)" : "var(--text-muted)" }}>
                    {step.label}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{step.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 32px 80px" }}>
        {/* ── Step 1: Identify ── */}
        {currentStep === "identify" && (
          <div className="animate-slide-up" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ padding: "16px 0" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Round 1: Independent Estimates</h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.6 }}>
                Each expert provides their low (10th percentile), median (50th), and high (90th percentile) estimates
                for the coefficient of <strong>{variable.name}</strong>. Estimates are private at this stage.
              </p>
            </div>

            {round1Experts.map((e) => (
              <ExpertCard key={e.expertId} expert={e} anonymous={false} showReasoning={false} />
            ))}

            <button
              onClick={() => setCurrentStep("discuss")}
              className="btn btn-filled"
              style={{ alignSelf: "flex-end", height: 44, padding: "0 28px", fontSize: 13 }}
            >
              Proceed to Discussion
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Step 2: Discuss ── */}
        {currentStep === "discuss" && (
          <div className="animate-slide-up" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ padding: "16px 0" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Anonymous Discussion</h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.6 }}>
                Experts can now see each other's <strong>anonymous</strong> estimates and reasoning.
                This prevents anchoring bias while enabling knowledge sharing. Identities are revealed only after
                the final round.
              </p>
            </div>

            {/* Anonymous overlay of all distributions */}
            <div className="card" style={{ padding: "20px 24px", borderRadius: 16 }}>
              <div className="label" style={{ marginBottom: 12 }}>Distribution Comparison (Anonymous)</div>
              <div style={{ position: "relative", height: 48, background: "rgba(0,0,0,0.02)", borderRadius: 8, overflow: "hidden" }}>
                {round1Experts.map((e, idx) => (
                  <div key={e.expertId} style={{
                    position: "absolute",
                    left: `${e.low * 100}%`,
                    right: `${(1 - e.high) * 100}%`,
                    top: 8 + idx * 12, height: 8,
                    background: ["rgba(196,181,253,0.4)", "rgba(147,197,253,0.4)", "rgba(134,239,172,0.4)"][idx],
                    borderRadius: 4,
                  }}>
                    <div style={{
                      position: "absolute",
                      left: `${((e.median - e.low) / (e.high - e.low)) * 100}%`,
                      top: 0, bottom: 0, width: 2,
                      background: "var(--text-primary)", borderRadius: 1,
                    }} />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: "var(--text-muted)" }}>
                <span>0.00</span>
                <span>0.25</span>
                <span>0.50</span>
                <span>0.75</span>
              </div>
            </div>

            {/* Anonymous reasoning cards */}
            {round1Experts.map((e) => (
              <ExpertCard key={e.expertId} expert={e} anonymous={true} showReasoning={true} />
            ))}

            <div style={{
              padding: "14px 18px", borderRadius: 14,
              background: "rgba(253,224,71,0.08)", border: "1px solid rgba(253,224,71,0.2)",
              fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.6,
            }}>
              <strong>Facilitator note:</strong> Key disagreement is on the magnitude of the effect of <em>{variable.name.replace(/_/g, " ")}</em>.
              Expert MK raises valid confounding concerns around socioeconomic factors and parental resources. Expert RP's courtroom experience supports a moderate effect on custody outcomes.
              Experts should consider whether their ranges adequately capture this uncertainty.
            </div>

            <button
              onClick={() => setCurrentStep("estimate")}
              className="btn btn-filled"
              style={{ alignSelf: "flex-end", height: 44, padding: "0 28px", fontSize: 13 }}
            >
              Proceed to Final Estimates
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Step 3: Estimate (Round 2) ── */}
        {currentStep === "estimate" && (
          <div className="animate-slide-up" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ padding: "16px 0" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Round 2: Revised Estimates</h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.6 }}>
                After seeing the anonymous discussion, each expert provides their <strong>final private estimate</strong>.
                Identities are now revealed. Note how estimates have converged after discussion.
              </p>
            </div>

            {/* Convergence indicator */}
            <div className="card" style={{ padding: "16px 20px", borderRadius: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div className="label" style={{ marginBottom: 4 }}>Expert Convergence</div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    Range narrowed from {(Math.max(...round1Experts.map(e => e.high)) - Math.min(...round1Experts.map(e => e.low))).toFixed(2)} to {(Math.max(...round2Experts.map(e => e.high)) - Math.min(...round2Experts.map(e => e.low))).toFixed(2)}
                  </div>
                </div>
                <div style={{
                  fontSize: 24, fontWeight: 300, color: "var(--text-primary)",
                }}>
                  {((1 - (Math.max(...round2Experts.map(e => e.high)) - Math.min(...round2Experts.map(e => e.low))) / (Math.max(...round1Experts.map(e => e.high)) - Math.min(...round1Experts.map(e => e.low)))) * 100).toFixed(0)}%
                  <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 4 }}>tighter</span>
                </div>
              </div>
            </div>

            {round2Experts.map((e) => (
              <ExpertCard key={e.expertId} expert={e} anonymous={false} showReasoning={true} />
            ))}

            <button
              onClick={() => setCurrentStep("aggregate")}
              className="btn btn-filled"
              style={{ alignSelf: "flex-end", height: 44, padding: "0 28px", fontSize: 13 }}
            >
              Aggregate Estimates
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Step 4: Aggregate ── */}
        {currentStep === "aggregate" && (
          <div className="animate-slide-up" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ padding: "16px 0" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Linear Pool Aggregation</h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.6 }}>
                Final estimates are combined using a <strong>linear opinion pool</strong> (equal weights).
                The aggregated distribution becomes the Bayesian prior for the shadow model pipeline.
                This follows the SHELF methodology for structured expert judgement.
              </p>
            </div>

            <AggregationChart experts={round2Experts} />

            {/* Quality assessment */}
            <div className="card" style={{ padding: 24, borderRadius: 20, textAlign: "center" }}>
              <div className="label" style={{ marginBottom: 16 }}>Elicitation Quality</div>
              <div style={{
                width: 72, height: 72, borderRadius: 20, margin: "0 auto 14px",
                background: "linear-gradient(135deg, rgba(134,239,172,0.2), rgba(134,239,172,0.05))",
                border: "1px solid rgba(255,255,255,0.5)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
              }}>
                <span style={{ fontSize: 32, fontWeight: 700, color: "var(--text-primary)" }}>A</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, maxWidth: 380, margin: "0 auto" }}>
                <strong>Excellent calibration.</strong> Expert estimates converged after discussion.
                Uncertainty is well-characterised across {round2Experts.length} independent assessors.
                The prior is ready for Bayesian updating.
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={onBack} className="btn btn-outline" style={{ height: 44, padding: "0 24px", fontSize: 13 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back to Results
              </button>
              <button
                onClick={() => onComplete({ mean: aggMean, std: aggStd, certainty: aggCertainty })}
                className="btn btn-filled"
                style={{ height: 44, padding: "0 28px", fontSize: 13, fontWeight: 500 }}
              >
                Use as Prior &rarr; Sandbox
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
